import React, { useMemo } from "react";
import { CCBadge, CCSpinner } from "@pymblecitizencoders/cc-ui";
import { CalendarClock } from "lucide-react";
import { CalendarEvent } from "../../types/event";
import { MAX_CLASH_SPAN_DAYS } from "../../hooks/useConcurrentEvents";
import { formatTimeRange } from "../../utils/eventDates";
import { groupByDay } from "../../utils/eventDays";
import { asDate, atMidnight } from "../../utils/eventWhen";
import { renderTitle } from "../../utils/eventTitle";
import "./eventManager.css";

interface EventClashPanelProps {
	/** Everything already running during the chosen window, earliest first. */
	events: CalendarEvent[];
	loading: boolean;
	failed: boolean;
	tooWide: boolean;
	/** Whole days the chosen window touches. 0 before a date is picked. */
	spanDays: number;
}

/**
 * What else is on while the event being created runs.
 *
 * ## It informs, it does not block
 *
 * The school runs plenty of things at once on purpose — a clash is a thing to
 * notice, not an error — so this never disables Add event and never colours
 * itself as a warning. The count is the whole message: someone about to put the
 * Year 9 camp on top of the swimming carnival will see it and change the date,
 * and someone deliberately adding a second Tuesday rehearsal will not be argued
 * with.
 *
 * ## One row per event, never repeated per day
 *
 * `groupByDay` files a multi-day event under the day it STARTS, which is what
 * is wanted here: a three-week excursion overlapping the window should read as
 * one thing that is on, not as twenty-one of them.
 */
export const EventClashPanel: React.FC<EventClashPanelProps> = ({
	events,
	loading,
	failed,
	tooWide,
	spanDays,
}) => {
	/* Day headings only earn their space once the window covers more than one
	   day — within a single day every row is that day, and the heading would be
	   a banner repeating what the form above already says. */
	const days = useMemo(
		() => (spanDays > 1 ? groupByDay(events) : []),
		[events, spanDays],
	);

	const body = () => {
		if (spanDays === 0) {
			return <p className="em-muted">Pick a date to see what else is on.</p>;
		}
		if (tooWide) {
			return (
				<p className="em-muted">
					That is longer than {MAX_CLASH_SPAN_DAYS} days, so what else is on
					is not worth listing — check the calendar instead.
				</p>
			);
		}
		if (failed) {
			return (
				<p className="em-error" role="alert">
					Could not check what else is on.
				</p>
			);
		}
		if (loading) {
			return (
				<p className="em-muted">
					<CCSpinner size="inline" /> Checking…
				</p>
			);
		}
		if (!events.length) {
			return <p className="em-muted">Nothing else is on at that time.</p>;
		}
		if (days.length) {
			return (
				<div className="em-clash__days">
					{days.map((day) => (
						<div key={day.key}>
							<h4 className="em-clash__day">{day.heading}</h4>
							<EventRows events={day.events} />
						</div>
					))}
				</div>
			);
		}
		return <EventRows events={events} />;
	};

	/**
	 * What a screen reader hears when the dates change.
	 *
	 * The live region is this one line and NOT the panel, which was the first
	 * version: every nudge of a time re-announced the whole list, so a busy
	 * Tuesday read out thirty event titles over the top of the field being
	 * typed into. The count is the message; the list is there to be read at
	 * leisure, and it sits in the tab order right after the form.
	 */
	const announcement = () => {
		// A failure already announces itself: the paragraph below is role="alert".
		if (spanDays === 0 || tooWide || loading || failed) return "";
		if (!events.length) return "Nothing else is on at that time.";
		return `${events.length} other ${
			events.length === 1 ? "event is" : "events are"
		} on at that time.`;
	};

	return (
		<aside className="em-clash">
			<span className="em-sr-only" role="status">
				{announcement()}
			</span>
			<h3 className="em-clash__title">
				<CalendarClock size={16} aria-hidden="true" />
				Also on
				{/* Only once there is a number to show: a badge reading 0 beside
				    "Nothing else is on at that time" says the same thing twice. */}
				{!loading && !failed && !tooWide && events.length > 0 && (
					<CCBadge tone="neutral">{events.length}</CCBadge>
				)}
			</h3>
			{body()}
		</aside>
	);
};

/** `23 Sep` — short, because it sits on the end of a line in a narrow column. */
const shortDate = (d: Date) =>
	d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });

/**
 * When an event runs, as one line.
 *
 * An all-day event is stored as midnight to 23:59, which `formatTimeRange`
 * reads literally as "12:00 am – 11:59 pm" — true, and not how anyone says it.
 * The Edumate feed's date-only events carry a start and no end at all, which
 * the same test catches.
 *
 * A row that finishes on a LATER DAY says so. Without it a three-day excursion
 * and a one-hour meeting read identically, and the row does not necessarily
 * belong to the day being looked at: the API's window is an overlap test, so
 * something that started last Thursday is here on its own merits.
 */
const whenOf = (evt: CalendarEvent): string => {
	const from = asDate(evt.start);
	if (!from) return "";
	const to = asDate(evt.end);
	/* 23:59 is what this app writes; midnight is what a feed sending an
	   exclusive end writes. Both mean the day, not a time in it. */
	const wholeDay =
		atMidnight(from) &&
		(!to || atMidnight(to) || (to.getHours() === 23 && to.getMinutes() >= 59));
	const label = wholeDay ? "All day" : formatTimeRange(evt.start, evt.end);
	if (!to || to.toDateString() === from.toDateString()) return label;
	return `${label} · until ${shortDate(to)}`;
};

/** The rows themselves, shared by the grouped and ungrouped layouts. */
const EventRows: React.FC<{ events: CalendarEvent[] }> = ({ events }) => (
	<ul className="em-clash__list">
		{events.map((evt) => (
			<li key={evt.id} className="em-clash__item">
				{/* The category colour the calendar already tints this event
				    with, so the two views agree on what an event "is". */}
				<span
					className="em-clash__dot"
					style={{ backgroundColor: evt.color }}
					aria-hidden="true"
				/>
				<span className="em-clash__main">
					<span className="em-clash__name">{renderTitle(evt.title)}</span>
					<span className="em-muted">
						{whenOf(evt)}
						{evt.location ? ` · ${evt.location}` : ""}
					</span>
				</span>
			</li>
		))}
	</ul>
);
